import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BreadCrumb } from "../common/BreadCrumb";
import { CustomSelect } from "../common/CustomSelect";
import {
  NavigationItem,
  NavigationSettings,
} from "../common/NavigationSettings";
import { CheckboxInput } from "../campaigns/CheckboxInput";
import { NumberInput } from "../campaigns/NumberInput";
import { TextInput } from "../campaigns/TextInput";
import { FeatureFlagService } from "../../services/FeatureFlagService";

const getNavigationSettings = () => {
  return new NavigationSettings({
    activeModule: new NavigationItem({
      identifier: "feature_flags",
      name: "Feature Flags",
    }),
  });
};

const hasValue = (values, key) =>
  Object.prototype.hasOwnProperty.call(values, key);

const getOverrideValue = (flag, overrideValues) =>
  hasValue(overrideValues, flag.key) ? overrideValues[flag.key] : flag.value;

const isValidOverrideValue = (value) =>
  value !== "" && value !== null && value !== undefined;

const getChangedValues = (featureFlags, overrideValues) => {
  return featureFlags.reduce((values, flag) => {
    const overrideValue = getOverrideValue(flag, overrideValues);
    if (overrideValue === flag.value) {
      return values;
    }

    return {
      ...values,
      [flag.key]: overrideValue,
    };
  }, {});
};

export const FeatureFlags = () => {
  const [featureFlags, setFeatureFlags] = useState([]);
  const [overrideValues, setOverrideValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const syncFeatureFlags = useCallback((flags) => {
    setFeatureFlags(flags);
    setOverrideValues(
      flags.reduce(
        (values, flag) => ({
          ...values,
          [flag.key]: flag.value,
        }),
        {}
      )
    );
  }, []);

  useEffect(() => {
    setLoading(true);
    new FeatureFlagService()
      .list()
      .then((response) => {
        if (response) {
          syncFeatureFlags(response.data);
        }
      })
      .finally(() => setLoading(false));
  }, [syncFeatureFlags]);

  const updateOverrideValue = (key, value) => {
    setOverrideValues((currentValues) => ({
      ...currentValues,
      [key]: value,
    }));
  };

  const changedValues = getChangedValues(featureFlags, overrideValues);
  const hasChanges = Object.keys(changedValues).length > 0;
  const hasInvalidChanges = Object.values(changedValues).some(
    (value) => !isValidOverrideValue(value)
  );

  const saveFeatureFlags = () => {
    setSaving(true);

    new FeatureFlagService()
      .update(changedValues)
      .then((response) => {
        if (response) {
          syncFeatureFlags(response.data);
          toast.success("Feature flags updated.");
        }
      })
      .finally(() => setSaving(false));
  };

  const renderOverrideInput = (flag) => {
    const value = getOverrideValue(flag, overrideValues);
    const availableValues = flag.availableValues || [];

    if (typeof flag.value === "boolean") {
      return (
        <CheckboxInput
          name={flag.key}
          checked={value}
          disabled={saving}
          onChange={(event) =>
            updateOverrideValue(flag.key, event.target.checked)
          }
        />
      );
    }

    if (typeof flag.value === "number") {
      return (
        <NumberInput
          name={flag.key}
          value={value}
          disabled={saving}
          onChange={(event) => {
            const nextValue = event.target.value;
            updateOverrideValue(
              flag.key,
              nextValue === "" ? "" : Number(nextValue)
            );
          }}
        />
      );
    }

    if (availableValues.length > 0) {
      return (
        <CustomSelect
          data={availableValues.map((availableValue) => ({
            name: availableValue,
            value: availableValue,
          }))}
          label="name"
          value="value"
          disabled={saving}
          onChange={(event) => updateOverrideValue(flag.key, event.value)}
          selectedValue={value}
        />
      );
    }

    return (
      <TextInput
        name={flag.key}
        value={value}
        disable={saving}
        onChange={(event) => updateOverrideValue(flag.key, event.target.value)}
      />
    );
  };

  return (
    <div className="main-content">
      <BreadCrumb navigationSettings={getNavigationSettings()} />
      <div className="section__content section__content--p30">
        <div className="container-fluid">
          <div className="container-fluid">
            <div className="row">
              <div className="col">
                {loading ? (
                  <div>Loading feature flags...</div>
                ) : (
                  <div>
                    <div className="mb-3 text-right">
                      <button
                        className="btn btn-primary"
                        onClick={saveFeatureFlags}
                        disabled={saving || !hasChanges || hasInvalidChanges}
                      >
                        Save
                      </button>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-bordered table-hover">
                        <thead>
                          <tr>
                            <th>Key</th>
                            <th>Current Value</th>
                            <th>Override Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {featureFlags.map((flag) => (
                            <tr key={flag.key}>
                              <td>{flag.key}</td>
                              <td>{String(flag.value)}</td>
                              <td>{renderOverrideInput(flag)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
